import ClassNames from 'classnames'
import * as React from 'react'
import * as moment from 'moment'
import { SingleDatePicker } from 'react-dates'
import styled from 'styled-components'
import I18n from '../../../core/i18n'
import { reservationService, utilService, userService } from '../../../core/services'
import * as constants from '../../../static/constants'
import * as interfaces from '../../../static/interfaces'
import * as Hooks from '../../../utils/hooks'
import { Button, InputText, InputTextArea, ListItem, Panel, Select } from '../../atoms'
import { Form } from '../../molecules'
import DayPickerCustomStyle from '../../../static/dayPickerCustomStyle'
import Counter from 'components/atoms/Counter'

import directReservationConfirmation from './directReservationConfirmation'
import Typography from 'components/atoms/_layouts/Typography'
import InputLabel from 'components/atoms/InputLabel'
import SidebarLayout from '../_layouts/sidebarLayout'
import ReservationPickupLocation from '../reservations/parts/atoms/ReservationPickupLocation'
import {
  CALENDAR_EVENTS_DEFAULT_MONTHS,
  useCalendarEvents,
  isCalendarOutsideRangeForCalendarEvents,
} from 'utils/useCalendarEvents'

declare var window: interfaces.IWindow

interface IProps {
  posts: interfaces.IPost[]
  user: any
}

interface IErrors {
  [key: string]: string | null
}

interface IDirectReservation {
  selectedDate: moment.Moment
  departure: interfaces.IStartTime
}

interface IDirectReservationFormProps {
  post: interfaces.IPost
  directReservation: IDirectReservation
  setIsCompleted(flag: boolean): void
  setCreatedDirectReservation(direct_reservation: any): void
}

const FIELDS = {
  price: 'price',
  guest_name: 'guest_name',
  guest_email: 'guest_email',
  memo: 'memo',
  pickup_location: 'pickup_location',
  pickup_latitude: 'pickup_latitude',
  pickup_longitude: 'pickup_longitude',
}

const getInitialPeopleCount = type =>
  type === 'private'
    ? { num_of_adults: 1 }
    : {
        num_of_adults: 1,
        num_of_children: 0,
        num_of_infants: 0,
      }

const DirectReservationForm: React.FC<IDirectReservationFormProps> = props => {
  const [isProcessing, setIsProcessing] = React.useState(false)
  const [errors, setErrors] = React.useState<IErrors>({})
  const [guests, setGuests] = React.useState(getInitialPeopleCount(props.post?.post_type))
  const [selectedDate, setSelectedDate] = React.useState<moment.Moment | null>(
    moment().add(1, 'days')
  )
  const [focus, setFocus] = React.useState<boolean>(false)
  const [selectedDeparture, setSelectedDeparture] = React.useState(null)
  const [noAvailableCalendar, setNoAvailableCalendar] = React.useState(false)
  const [zeroPeople, setZeroPeople] = React.useState<boolean>(false)
  const [price, setPrice] = React.useState('0')

  const getParticipantTypePrice = React.useCallback(
    (type: string): number => {
      const item = props.post?.post_prices.find(({ participant_type }) => participant_type === type)
      return item ? item.price : 0
    },
    [props.post]
  )

  React.useEffect(() => {
    // props.post.post_calendars.length === 0 ||
    if (props.post.post_start_times.length === 0) {
      setNoAvailableCalendar(true)
    }
  }, [props.post])
  React.useEffect(() => {
    setZeroPeople(reservationService.getGuestCount(guests) === 0)
  }, [guests])

  const getAgeRange = (type: string): string => {
    const item = props.post?.post_prices.find(({ participant_type }) => participant_type === type)
    return item ? `(${item.age_min}~${item.age_max} years old)` : ''
  }

  const handleUpdateForm = (updatedErrors, isSubmitEnabled) => {
    setErrors(updatedErrors)
  }

  const handleSubmit = async (initialValues, values) => {
    const post_data = {
      name: props.post.name,
      overview: props.post.overview,
      highlight: props.post.highlight,
      itinerary: props.post.itinerary,
    }
    const start_at = `${selectedDate.format('YYYY/MM/DD')} ${selectedDeparture?.start_time}:00`
    const end_at = `${moment(start_at)
      .add(props.post?.duration, 'hours')
      .format('YYYY/MM/DD HH:mm:ss')}`
    const params = {
      direct_reservation: {
        post_start_time_id: selectedDeparture.post_start_time_id,
        post_calendar_id: selectedDeparture.post_calendar_id,
        post_id: props.post.id,
        start_at,
        end_at,
        price: values.price,
        guest_name: values.guest_name,
        guest_email: values.guest_email,
        memo: values.memo,
        pickup_location: values.pickup_location,
        pickup_latitude: values.pickup_latitude,
        pickup_longitude: values.pickup_longitude,
        ...guests,
      },
    }
    directReservationConfirmation(post_data, params, props)
  }

  const changeNumOfAdult = newValue => {
    handlePeopleEdit('num_of_adults', newValue)
  }
  const changeNumOfChildren = newValue => {
    handlePeopleEdit('num_of_children', newValue)
  }
  const changeNumOfInfants = newValue => {
    handlePeopleEdit('num_of_infants', newValue)
  }
  const handlePeopleEdit = (name, newValue) => {
    setGuests(current => {
      const nextPeople = { ...current }
      nextPeople[name] = newValue
      // update Price with new Value
      updatePrice(nextPeople)
      return nextPeople
    })
  }
  const { calendarEvents, getCalendarEvents } = useCalendarEvents()

  React.useEffect(() => {
    getCalendarEvents(props.post?.id, moment().add(1, 'days'), CALENDAR_EVENTS_DEFAULT_MONTHS, {
      includePast: true,
    })
  }, [])

  const onDateRangePickerMonthChange = async changedMoment => {
    getCalendarEvents(props.post?.id, changedMoment, CALENDAR_EVENTS_DEFAULT_MONTHS, {
      includePast: true,
    })
  }

  const handleCalendarOutSideRange = calendarMoment => {
    return isCalendarOutsideRangeForCalendarEvents(calendarEvents, calendarMoment)
  }

  const selectedDayStartTimes = React.useMemo(() => {
    if (!calendarEvents || !selectedDate) {
      return null
    }

    const day = calendarEvents.find(calendarEvent => selectedDate.isSame(calendarEvent.date, 'day'))

    if (day) {
      return day.start_times
    } else {
      return []
    }
  }, [calendarEvents, selectedDate])

  React.useEffect(() => {
    if (selectedDayStartTimes?.length) {
      const availables = selectedDayStartTimes.filter(
        ({ available_count, reserved }) => available_count > 0 && !reserved
      )
      setSelectedDeparture({ ...availables[0] })
    }
  }, [selectedDayStartTimes])

  const updatePrice = new_guests => {
    const new_price_adult = getParticipantTypePrice('adult') * new_guests.num_of_adults
    const new_price_children = getParticipantTypePrice('child') * new_guests.num_of_children || 0
    const new_price_infants = getParticipantTypePrice('infant') * new_guests.num_of_infants || 0
    const new_price = new_price_adult + new_price_children + new_price_infants
    const price_string = utilService.formatPriceForForm(new_price)

    setPrice(price_string)
  }

  const changePrice = value => {
    setPrice(value)
  }

  const maxCount = React.useMemo(() => Number(props.post.booking_max_people), [
    props.post.booking_max_people,
  ])
  const isLimit = React.useMemo(() => {
    const totalPeople = reservationService.getGuestCount(guests)
    return totalPeople >= maxCount
  }, [guests])

  return (
    <Form fields={FIELDS} handleSubmit={handleSubmit} handleUpdateForm={handleUpdateForm}>
      <ListItem>
        {props.post.post_start_times.length === 0 && (
          <div className="Empty_Start_Time">
            {I18n.t(
              'activerecord.errors.models.direct_reservation.post_has_no_calendar_or_starttime'
            )}
          </div>
        )}
        <div className="Custom_Input">
          <InputLabel label={I18n.t('direct_reservation.reservation_datetime')} required={true} />
          <div className="Custom_Input_Content">
            <DayPickerCustomStyle />
            <SingleDatePicker
              noBorder={false}
              date={selectedDate}
              onDateChange={date => {
                setSelectedDate(date)
              }}
              focused={focus}
              initialVisibleMonth={() => selectedDate}
              enableOutsideDays={false}
              numberOfMonths={1}
              hideKeyboardShortcutsPanel={true}
              isOutsideRange={handleCalendarOutSideRange}
              onPrevMonthClick={onDateRangePickerMonthChange}
              onNextMonthClick={onDateRangePickerMonthChange}
              onFocusChange={date => {
                setFocus(date.focused)
              }}
            />
            {selectedDate && (
              <div className="Start_Time">
                <span className="StartTimes_Label">{I18n.t('generic.start_time')}</span>
                <div className="StartTimes_List">
                  {selectedDayStartTimes.map((startTime, index) => (
                    <div
                      key={index}
                      className={ClassNames('StartTimes_Item', {
                        selected: selectedDeparture?.start_time === startTime.start_time,
                        closed: startTime.reserved || startTime.available_count === 0,
                      })}
                      onClick={() => {
                        if (startTime.reserved || startTime.available_count === 0) {
                          return
                        }
                        setSelectedDeparture({ ...startTime })
                      }}
                    >
                      {startTime.start_time}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {noAvailableCalendar && (
              <div className="DirectReservationNew_CalendarButtons">
                <a
                  className="DirectReservationNew_CalendarButton"
                  href={`/agent/posts/${props.post.slug}/edit?active=calendar`}
                >
                  {I18n.t('direct_reservation.create_post_calendar')}
                </a>
                <br />
                <a
                  className="DirectReservationNew_CalendarButton"
                  href={`/agent/posts/${props.post.slug}/edit?active=startTime`}
                >
                  {I18n.t('direct_reservation.create_post_start_time')}
                </a>
              </div>
            )}
          </div>
        </div>
      </ListItem>
      <ListItem>
        <div className="Custom_Input">
          <InputLabel label={I18n.t('direct_reservation.number_of_guests')} required={true} />
          <div className="Custom_Input_Content">
            {props.post?.post_type === 'private' ? (
              <div className="MaxPeople">
                <div className="MaxPeople_Label">
                  <div>{I18n.t('generic.guests')}</div>
                </div>
                <Counter
                  defaultValue={guests.num_of_adults}
                  handleChange={changeNumOfAdult}
                  minValue={1}
                  isLimit={isLimit}
                />
              </div>
            ) : (
              <div className="MaxPeople_List">
                <div className="MaxPeople">
                  <div className="MaxPeople_Label">
                    <div>{I18n.t('generic.adult')}</div>
                    <span>¥{getParticipantTypePrice('adult')}</span>
                  </div>
                  <Counter
                    defaultValue={guests.num_of_adults}
                    handleChange={changeNumOfAdult}
                    isLimit={isLimit}
                  />
                </div>

                <div className="MaxPeople">
                  <div className="MaxPeople_Label">
                    <div>
                      {I18n.t('generic.child')}
                      {getAgeRange('child')}
                    </div>
                    <span>¥{getParticipantTypePrice('child')}</span>
                  </div>
                  <Counter
                    defaultValue={guests.num_of_children}
                    handleChange={changeNumOfChildren}
                    isLimit={isLimit}
                  />
                </div>

                <div className="MaxPeople">
                  <div className="MaxPeople_Label">
                    <div>
                      {I18n.t('generic.infant')}
                      {getAgeRange('infant')}
                    </div>
                    <span>¥{getParticipantTypePrice('infant')}</span>
                  </div>
                  <Counter
                    defaultValue={guests.num_of_infants}
                    handleChange={changeNumOfInfants}
                    isLimit={isLimit}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      </ListItem>
      <ListItem>
        <InputText
          label={I18n.t('direct_reservation.price')}
          name="price"
          value={price}
          onChangeHandler={event => changePrice(event.target.value)}
          required={true}
        />
      </ListItem>
      <ListItem>
        <Typography type="headline" size="sm">
          {I18n.t('direct_reservation.guest_info')}
        </Typography>
      </ListItem>
      <ListItem>
        <InputText
          name="guest_name"
          defaultValue=""
          required={true}
          label={I18n.t('direct_reservation.guest_name')}
        />
      </ListItem>
      <ListItem>
        <InputText
          name="guest_email"
          defaultValue=""
          required={false}
          label={I18n.t('direct_reservation.guest_email')}
        />
      </ListItem>
      <ListItem>
        <InputTextArea
          name="memo"
          defaultValue=""
          required={false}
          label={I18n.t('direct_reservation.memo')}
        />
      </ListItem>
      <ListItem>
        <ReservationPickupLocation />
      </ListItem>
      <div className="directReservation_notice">
        <Typography type="body">{I18n.t('direct_reservation.note')}</Typography>
      </div>
      <Button
        disabled={
          isProcessing || noAvailableCalendar || zeroPeople || !selectedDeparture?.available_count
        }
        primary={true}
      >
        {I18n.t('generic.preview')}
      </Button>
    </Form>
  )
}

const DirectReservationNew: React.FC<IProps> = props => {
  Hooks.SetDocumentTitle(I18n.t('direct_reservation.create_direct_reservation'))
  const [isCompleted, setIsCompleted] = React.useState(false)
  const [createdDirectReservation, setCreatedDirectReservation] = React.useState(null)
  const [selectedPost, setSelectedPost] = React.useState(props.posts[0])
  const { user } = userService.getUserFromJson(props.user)

  const postOptions = props.posts.map(post => {
    return { label: post.name, value: post.id }
  })

  const onSelectPost = value => {
    let newSelectedPost = null

    props.posts.map(post_item => {
      const value_int = parseInt(value, 10)
      if (post_item.id === value_int) {
        newSelectedPost = post_item
      }
    })
    setSelectedPost(newSelectedPost)
  }

  return props.posts.length === 0 ? (
    <DirectReservationNewWrapper>
      <div className="No_Post">
        <h2>{I18n.t('activerecord.errors.models.direct_reservation.user_has_no_posts')}</h2>
      </div>
    </DirectReservationNewWrapper>
  ) : (
    <SidebarLayout user={user}>
      <DirectReservationNewWrapper>
        <Panel
          title={I18n.t('direct_reservation.create_direct_reservation')}
          marginType="dashboard"
        >
          {isCompleted ? (
            <div className="DirectReservationNew_Completed">
              <h2 className="DirectReservationNew_CompletedHeader">
                {I18n.t('direct_reservation.direct_reservation_completed')}
                <br />
                <a
                  href="/agent/direct_reservations/new"
                  className="DirectReservationNew_AnotherButton"
                >
                  {I18n.t('direct_reservation.direct_reservation_new')}
                </a>
                <br />
                <a href="/agent/host_reservations" className="DirectReservationNew_AnotherButton">
                  {I18n.t('direct_reservation.reservation_index')}
                </a>
              </h2>
            </div>
          ) : (
            <>
              <Select
                name="post"
                options={postOptions}
                required={true}
                onChangeHandler={event => onSelectPost(event.target.value)}
                defaultValue={selectedPost?.id + ''}
                label={I18n.t('generic.tour_required')}
              />
              <div className="Form_Section">
                <DirectReservationForm
                  post={selectedPost}
                  directReservation={createdDirectReservation} // props.directReservation
                  setCreatedDirectReservation={setCreatedDirectReservation}
                  setIsCompleted={setIsCompleted}
                  key={selectedPost.id}
                />
              </div>
            </>
          )}
        </Panel>
      </DirectReservationNewWrapper>
    </SidebarLayout>
  )
}

const DirectReservationNewWrapper = styled.div`
  h2 {
    font-size: 20px;
    margin: 0px auto 10;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin: 10px auto 0;
    }
  }

  .No_Post {
    margin: 10px;
    min-height: 300px;
  }
  .Empty_Start_Time {
    margin: 10px 0;
    font-size: 14px;
    font-weight: bold;
    color: ${constants.COLORS.caution};
  }

  .MaxPeople {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 400px;
  }

  .MaxPeople_Label {
    font-size: 16px;
    font-weight: bold;
    width: 200px;
    > span {
      display: block;
      font-weight: normal;
    }
  }

  .MaxPeople_List {
    display: flex;
    flex-direction: column;
    gap: 16px;
    width: 100%;
  }

  .Form_Section {
    margin-left: auto;
    margin-top: 16px;
    @media (max-width: ${constants.BREAKPOINT_DESKTOP}px) {
      margin-left: 0;
    }
  }
  .Custom_Input {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
  }

  .Custom_Input_Content {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2em;
    width: 100%;
    max-width: 400px;
  }

  .Start_Time {
    display: flex;
    align-items: center;
  }

  .Guest_Number {
    display: flex;
    align-items: center;
    margin: 1em 0;
  }

  .Price {
    display: flex;
    align-items: center;
    margin: 1em 0;
  }

  .DirectReservationNew_CompletedHeader {
    margin-bottom: 24px;
    text-align: center;
    font-size: 20px;
  }

  .DirectReservationNew_CompletedButtons {
    margin: 24px 0;
    display: flex;
    justify-content: center;
  }

  .DirectReservationNew_CalendarButtons {
    width: 100%;
    display: flex;
    gap: 8px;
  }

  .DirectReservationNew_CalendarButton {
    height: 36px;
    padding: 0 5px;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    line-height: 1;
    font-size: 14px;
    width: 50%;
    display: inline-block;
    text-align: center;
    &:hover {
      opacity: 0.6;
    }

    background-color: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
    border: none;
    margin: 5px;
    padding: 10px;

    > a {
      color: ${constants.COLORS.white};
      display: inline-block;
      text-align: center;
      height: inherit;
      text-decoration: none;
    }
  }

  .DirectReservationNew_AnotherButton {
    height: 36px;
    padding: 0 16px;
    border-radius: 4px;
    outline: none;
    cursor: pointer;
    line-height: 1;
    font-size: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    &:hover {
      opacity: 0.6;
    }

    background-color: ${constants.COLORS.primary};
    color: ${constants.COLORS.white};
    border: none;
    margin: 20px;
    padding: 10px;

    > a {
      color: ${constants.COLORS.white};
      display: flex;
      justify-content: center;
      align-items: center;
      height: inherit;
      text-decoration: none;
    }
  }

  .Button {
    margin-top: 24px;
  }

  .directReservation_notice {
    padding: 16px 0;
    white-space: pre-wrap;
    margin-left: 180px;
    @media (max-width: ${constants.BREAKPOINT_TABLET_SMALL}px) {
      margin-left: 0;
    }
  }

  .InputText_Required {
    display: inline-block;
    border-radius: 3px;
    color: ${constants.COLORS.white};
    background-color: ${constants.COLORS.caution};
    vertical-align: middle;
    font-size: 11px;
    font-weight: normal;
    padding: 4px 6px;
    margin-left: 8px;
  }

  .StartTimes_Label {
    display: block;
    font-size: 12px;
  }

  .StartTimes_List {
    display: flex;
    margin-top: 8px;
    flex-wrap: wrap;
    max-width: 318px;
  }

  .StartTimes_Item {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 62px;
    height: 36px;
    margin: 0 6px 6px;
    border: solid 1px ${constants.COLORS.divider};
    font-size: 16px;
    border-radius: 3px;
    cursor: pointer;

    &.closed {
      background-color: ${constants.COLORS.disabled};
      border: none;
    }

    &.selected {
      background-color: ${constants.COLORS.primary};
      border: none;
      color: ${constants.COLORS.white};
    }
  }

  .Guest_Info {
    margin-top: 32px;
    input {
      margin: 10px 0;
    }
  }

  .DayPickerWrapper {
    height: 320px;
    .DayPicker {
      margin-left: 0;
    }
  }
`

export default DirectReservationNew
